/**
 * Engagement Score Calculation Utility
 * Combines scroll depth, time on page, and interactions into a weighted score
 */

// Page types affect time expectations
export type PageType = 'article' | 'dashboard' | 'interactive' | 'form';

// Time thresholds by page type (seconds for max score)
const TIME_THRESHOLDS: Record<PageType, number> = {
  article: 180,      // 3 min for long-form content
  dashboard: 60,     // 1 min for dashboard overview
  interactive: 120,  // 2 min for interactive features
  form: 90,          // 1.5 min for form pages
};

// Map routes to page types
const PAGE_TYPE_MAP: Record<string, PageType> = {
  '/dashboard': 'dashboard',
  '/vision-2040': 'article',
  '/methodology': 'article',
  '/faq': 'article',
  '/contributing': 'article',
  '/white-paper': 'article',
  '/executive-brief': 'article',
  '/member-insights': 'interactive',
  '/regional-analysis': 'interactive',
  '/impact-metrics': 'interactive',
  '/mental-health-dimension': 'interactive',
  '/workforce-education': 'interactive',
  '/entrepreneurship-support': 'interactive',
  '/programs-events': 'interactive',
  '/technology-future': 'interactive',
  '/development-councils': 'interactive',
  '/settings': 'form',
};

/**
 * Get the page type for a given pathname
 */
export function getPageType(pathname: string): PageType {
  return PAGE_TYPE_MAP[pathname] || 'dashboard';
}

/**
 * Calculate scroll score (0-100) based on max depth reached
 */
export function calculateScrollScore(maxDepthReached: number): number {
  return Math.min(100, Math.max(0, Math.round(maxDepthReached)));
}

/**
 * Calculate time score (0-100) based on seconds spent
 * Uses logarithmic scale to reward initial engagement more
 */
export function calculateTimeScore(
  seconds: number,
  pageType: PageType = 'dashboard'
): number {
  const threshold = TIME_THRESHOLDS[pageType];
  // Logarithmic scale to reward initial engagement
  const normalizedTime = Math.min(seconds / threshold, 1);
  return Math.round(normalizedTime * 100);
}

/**
 * Calculate combined engagement score (0-100)
 * Formula: (Scroll * 0.4) + (Time * 0.4) + (Interactions * 0.2)
 */
export function calculateEngagementScore(
  scrollScore: number,
  timeScore: number,
  interactionCount: number
): number {
  // Cap interaction bonus at 100 (5+ interactions = max)
  const interactionBonus = Math.min(interactionCount * 20, 100);

  // Weighted formula
  const score = (scrollScore * 0.4) + (timeScore * 0.4) + (interactionBonus * 0.2);

  return Math.round(Math.min(100, Math.max(0, score)));
}

export type EngagementCategory =
  | 'highly_engaged'
  | 'engaged'
  | 'moderate'
  | 'low'
  | 'bounce';

/**
 * Get engagement category based on score
 */
export function getEngagementCategory(score: number): EngagementCategory {
  if (score >= 80) return 'highly_engaged';
  if (score >= 60) return 'engaged';
  if (score >= 40) return 'moderate';
  if (score >= 20) return 'low';
  return 'bounce';
}

/**
 * Get human-readable description for engagement category
 */
export function getEngagementDescription(category: EngagementCategory): string {
  const descriptions: Record<EngagementCategory, string> = {
    highly_engaged: 'Deep scroll + long time + interactions',
    engaged: 'Good scroll or time, some interactions',
    moderate: 'Partial engagement with content',
    low: 'Minimal scrolling, short visit',
    bounce: 'Quick exit, little to no engagement',
  };
  return descriptions[category];
}
